/**
 * cart-discount-sync.js
 *
 * Single source of truth for the "free gift + tiered discount" feature.
 *
 * Rule:
 *   - cart total (excluding the gift item) >= THRESHOLD
 *       -> apply HIGH_DISCOUNT code, add the gift variant
 *   - cart total (excluding the gift item) < THRESHOLD
 *       -> apply LOW_DISCOUNT code, remove the gift variant if present
 *
 * Fully automatic — no "Apply" button. Management starts the moment the
 * cart has its first item (a genuinely empty cart is left untouched) and
 * is re-checked on every Shopify cartUpdate pub/sub event (fired by
 * Dawn's cart-items.js / quantity-popover.js after every add/remove/
 * update). No MutationObserver, no second engine competing for the same
 * job -> no duplicate/cascading network calls.
 *
 * #discountCode is a status indicator only (disabled, no click handler) —
 * it always reads "Applied" once a managed discount is live on the cart.
 *
 * Confetti (launchConfettiFromButton()) fires on:
 *   1. The free gift being freshly earned (cart had zero gift lines and
 *      just became eligible) — repeatable, fires every time this happens.
 *   2. The very first time this cart goes from "no discount managed" to
 *      "a discount applied". Tracked locally (sessionStorage), not from
 *      the live cart API — deterministic across empty -> add -> remove
 *      to empty -> add-again.
 * It does NOT fire on a downgrade, and it does NOT fire when we're just
 * silently deduplicating an already-earned gift (see below).
 *
 * NETWORK BEHAVIOUR (minimised):
 *   - Quantity changes inside the cart already publish the FULL cart
 *     object on `cartUpdate` (cart-items.js gets it from /cart/change.js).
 *     We reuse that object directly instead of doing our own GET
 *     /cart.js — a quantity change that needs no correction costs ZERO
 *     extra requests from this file.
 *   - "Add to cart" (product-form.js) only publishes the newly added
 *     line item, not the full cart, so that trigger still does one GET
 *     /cart.js to get authoritative totals/discount state.
 *   - When a correction IS needed, the discount code change and the
 *     gift add/remove/fix are sent in a SINGLE POST /cart/update.js
 *     call, and that same call also requests rendered `sections` HTML —
 *     so no second GET is needed just to re-paint the drawer.
 *   - A defensive fallback (separate /cart/add.js) only fires if the
 *     gift variant, against expectation, wasn't added by the combined
 *     call above.
 *
 * DUPLICATE GIFT LINE FIX (this revision):
 *   Two page loads can race each other: e.g. an "Add to cart" on a
 *   product page crosses the free-gift threshold and kicks off a
 *   correction, the shopper hits the browser Back button before that
 *   finishes, and the page they land on runs its own page-load sync at
 *   nearly the same moment. Both independently see "no gift yet" and
 *   both add it — the in-memory `isSyncing` lock only protects a single
 *   page instance, it can't see across that navigation. Shopify can end
 *   up with the gift split across two separate cart lines rather than
 *   one line with quantity 2.
 *   Fix: every sync now looks at ALL lines matching the gift variant
 *   (not just the first), and fixes them by their unique line `key`
 *   (not variant ID, which is ambiguous once duplicates exist). If more
 *   than one gift line is ever found, every one of them gets zeroed and
 *   a single clean unit is re-added if still eligible. This makes the
 *   cart self-healing: any duplicate already sitting in a cart cleans
 *   itself up the next time this script runs (page load, cart open, any
 *   quantity change) — no manual cleanup needed.
 *
 * Load this once, after cart.js:
 *   <script src="{{ 'cart-discount-sync.js' | asset_url }}" defer="defer"></script>
 */
(function () {
  'use strict';

  // ---- Config -------------------------------------------------------
  const GIFT_VARIANT_ID = '51631216558358';
  const HIGH_DISCOUNT = 'GOLDNOSEPIN';
  const LOW_DISCOUNT = 'PURE10';
  const THRESHOLD = 3000; // shop currency major units (e.g. ₹3000)
  const MANAGED_CODES = [HIGH_DISCOUNT.toLowerCase(), LOW_DISCOUNT.toLowerCase()];

  // ---- State guards ---------------------------------------------------
  let isSyncing = false;
  let pendingResync = false;
  let pendingHint; // full cart object from the latest cartUpdate event, if usable
  let debounceId = null;
  let fireworkOnGift = false;
  let fireworkOnFirstDiscount = false;

  // ---- Helpers ----------------------------------------------------------
  function cartTotalWithoutGift(cart) {
    return (
      cart.items.reduce((sum, item) => {
        return String(item.variant_id) === GIFT_VARIANT_ID
          ? sum
          : sum + item.original_price * item.quantity;
      }, 0) / 100
    );
  }

  function computeDesiredState(cart) {
    const currentDiscount = cart.discount_codes?.find((d) => d.applicable);
    const currentCode = currentDiscount?.code?.toLowerCase() || null;
    const isForeignDiscount = !!currentCode && !MANAGED_CODES.includes(currentCode);

    const eligible = cartTotalWithoutGift(cart) >= THRESHOLD;

    // ALL lines for the gift variant, not just the first — a duplicate
    // from a navigation race would otherwise hide behind .find().
    const giftLines = cart.items.filter((item) => String(item.variant_id) === GIFT_VARIANT_ID);
    const giftQtyTotal = giftLines.reduce((sum, line) => sum + line.quantity, 0);
    const desiredGiftQty = eligible ? 1 : 0;
    const desiredCode = eligible ? HIGH_DISCOUNT : LOW_DISCOUNT;

    return {
      isForeignDiscount,
      currentCode,
      desiredCode,
      eligible,
      giftLines,
      needsDiscountChange: !isForeignDiscount && currentCode !== desiredCode.toLowerCase(),
      // Anything off — wrong total quantity, OR the gift split across
      // more than one line — gets fully reconciled: every existing gift
      // line is zeroed by its own key, then a single clean unit is
      // re-added if still eligible.
      needsGiftFix: giftLines.length > 1 || giftQtyTotal !== desiredGiftQty,
      // Separate from needsGiftFix: only true when the cart had ZERO
      // gift lines and just became eligible. Used purely to gate
      // confetti so a silent dedupe of an already-earned gift doesn't
      // re-fire it.
      needsFreshGiftEarn: giftLines.length === 0 && eligible,
    };
  }

  function getDrawerSectionIds() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return drawer.getSectionsToRender().map((s) => s.id); // ['cart-drawer', 'cart-icon-bubble']
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  // ---- Apply corrections in ONE round trip when possible -----------------
  async function applyState(state) {
    const body = {};
    if (state.needsDiscountChange) body.discount = state.desiredCode;

    if (state.needsGiftFix) {
      const updates = {};
      // Zero every existing gift line by its own unique key first — key
      // always targets exactly one specific line, unlike variant ID,
      // which is ambiguous once duplicates exist.
      state.giftLines.forEach((line) => {
        updates[line.key] = 0;
      });
      if (state.eligible) {
        // /cart/update.js treats `updates` as "set this variant's
        // quantity" — if it isn't in the cart yet (e.g. we just zeroed
        // every duplicate above), it gets added fresh.
        updates[GIFT_VARIANT_ID] = 1;
      }
      body.updates = updates;
    }

    if (Object.keys(body).length === 0) return null;

    // Ask for rendered section HTML in the same response, so we don't
    // need a second request just to re-paint the drawer afterwards.
    body.sections = getDrawerSectionIds();
    body.sections_url = window.location.pathname;

    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    let updatedCart = await res.json();

    if (state.eligible && state.needsGiftFix) {
      const giftPresent = updatedCart.items.some(
        (i) => String(i.variant_id) === GIFT_VARIANT_ID && i.quantity > 0
      );
      if (giftPresent) {
        if (state.needsFreshGiftEarn) fireworkOnGift = true;
      } else {
        // Fallback: the combined-update trick above didn't add the gift
        // on this store — add it the normal way, then grab one fresh
        // snapshot.
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: GIFT_VARIANT_ID, quantity: 1 }] }),
        });
        if (state.needsFreshGiftEarn) fireworkOnGift = true;
        const [freshCart, sectionsJson] = await Promise.all([
          fetch('/cart.js').then((r) => r.json()),
          fetch(`${window.Shopify.routes.root}cart?sections=${getDrawerSectionIds().join(',')}`).then((r) =>
            r.json()
          ),
        ]);
        updatedCart = Object.assign({}, freshCart, { sections: sectionsJson });
      }
    }

    return updatedCart;
  }

  function paintSections(sectionsPayload) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !sectionsPayload || typeof drawer.renderContents !== 'function') return;

    // Only suppress the auto-open when the drawer is ALREADY open (a
    // silent background correction). If it's still closed, let
    // renderContents() open it for real (e.g. the very first item
    // landing in an empty cart).
    const wasAlreadyOpen = drawer.classList.contains('active');
    const originalOpen = drawer.open.bind(drawer);
    if (wasAlreadyOpen) {
      drawer.open = () => {};
    }

    drawer.renderContents({ sections: sectionsPayload });

    const cartItems = drawer.querySelector('cart-drawer-items');
    drawer.classList.toggle('is-empty', !cartItems || cartItems.querySelectorAll('.cart-item').length === 0);

    if (fireworkOnGift || fireworkOnFirstDiscount) {
      fireworkOnGift = false;
      fireworkOnFirstDiscount = false;
      if (typeof launchConfettiFromButton === 'function') launchConfettiFromButton();
    }

    if (wasAlreadyOpen) {
      setTimeout(() => {
        drawer.open = originalOpen;
      }, 50);
    }
  }

  // ---- "Applied" status indicator (no click handler, always reflects state) --
  function applyButtonAppliedStyle() {
    const button = document.querySelector('#discountCode');
    if (!button) return;
    button.textContent = 'Applied';
    button.disabled = true;
    button.style.background = 'transparent';
    button.style.color = '#28a745';
    button.style.opacity = '1';
    button.style.fontWeight = '600';
  }

  function updateApplyButtonUI(finalCode) {
    if (finalCode) {
      sessionStorage.setItem('discount_applied', 'true');
      applyButtonAppliedStyle();
    } else {
      sessionStorage.removeItem('discount_applied');
    }
  }

  // Cheap, synchronous, no network — just re-paints the indicator from
  // sessionStorage. Call this the instant the drawer's DOM is replaced
  // (by us or by Dawn's own cart-items re-render) so there's no visible
  // flash back to a "not applied" state while the real sync runs.
  function restoreButtonStyleIfApplied() {
    if (sessionStorage.getItem('discount_applied') === 'true') {
      applyButtonAppliedStyle();
    }
  }

  function showBannerMessage(message, type) {
    const el = document.querySelector('#discountMessage');
    if (!el) return;
    el.textContent = message;
    el.className = `discount-message ${type}`;
    el.style.display = 'block';
  }

  // ---- Core sync --------------------------------------------------------
  // cartHint: when the caller already has the full cart object (from a
  // cart-items.js cartUpdate event), pass it in to skip our own GET /cart.js.
  async function sync(cartHint) {
    console.log(cartHint);
    if (isSyncing) {
      pendingResync = true;
      return;
    }
    isSyncing = true;

    try {
      const cart =
        cartHint && Array.isArray(cartHint.items) ? cartHint : await fetch('/cart.js').then((r) => r.json());

      // A genuinely empty cart is left alone — no discount code, no gift,
      // no drawer touching. Reset our local "have we managed this cart"
      // flag here, so the next item added is treated as a fresh
      // first-ever event again.
      if (cart.item_count === 0) {
        sessionStorage.removeItem('discount_applied');
        return;
      }

      const state = computeDesiredState(cart);
      const changed = state.needsDiscountChange || state.needsGiftFix;

      // Blank-cart -> first-item-added -> first-discount-ever moment.
      // Tracked in sessionStorage (not derived from cart.discount_codes,
      // which can lag right after we POST a new code) — true only until
      // WE have successfully established a managed discount, and it only
      // resets when the cart goes empty.
      const wasManaged = sessionStorage.getItem('discount_applied') === 'true';
      const isFirstEverDiscount = !wasManaged;

      if (changed) {
        if (isFirstEverDiscount) {
          fireworkOnFirstDiscount = true;
        }
        const updatedCart = await applyState(state);
        if (updatedCart && updatedCart.sections) {
          paintSections(updatedCart.sections);
        }
        restoreButtonStyleIfApplied();
      } else if (isFirstEverDiscount) {
        // Nothing needed to be sent to Shopify — the discount code was
        // still attached to the cart from before it went empty. Still
        // counts as the celebratory first-ever moment.
        if (typeof launchConfettiFromButton === 'function') launchConfettiFromButton();
      }

      const finalCode = state.isForeignDiscount ? state.currentCode : state.desiredCode;
      updateApplyButtonUI(finalCode);

      if (isFirstEverDiscount && finalCode) {
        showBannerMessage(`Discount applied: ${finalCode.toUpperCase()}`, 'success');
      }
    } catch (err) {
      console.error('Cart discount sync failed:', err);
    } finally {
      isSyncing = false;
      document.querySelectorAll('cart-drawer').forEach((celem) => celem.classList.remove('loading_state'));
      if (pendingResync) {
        pendingResync = false;
        scheduleSync(pendingHint);
      }
    }
  }

  function scheduleSync(cartHint) {
    pendingHint = cartHint;
    clearTimeout(debounceId);
    debounceId = setTimeout(() => sync(pendingHint), 50);
  }

  // ---- Trigger: page load + Dawn's own cart-update pub/sub only ---------
  document.addEventListener('DOMContentLoaded', () => {
    sync(); // correct any mismatch already present on page load

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        restoreButtonStyleIfApplied(); // instant, no flicker while the full sync runs
        document.querySelectorAll('cart-drawer').forEach((celem) => celem.classList.add('loading_state'));

        // cart-items.js (qty +/-, remove) publishes the FULL cart object
        // here (from /cart/change.js) — reuse it, skip our own GET /cart.js.
        // product-form.js (Add to cart) only publishes the added line
        // item, not the full cart, so there's no usable hint there.
        const hint = event && event.cartData && Array.isArray(event.cartData.items) ? event.cartData : undefined;

        scheduleSync(hint);
      });
    }
  });
})();